import {
 GridFilterModel,
 GridPaginationModel,
 GridSortModel,
} from '@mui/x-data-grid-premium';
import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { ColPresetType } from '../../../../redux/clientConfiguration/clientConfigurationSlice';
import { StructureType } from '../../../../types/types';
import { CLIENT_LOAD_ALL_LIMIT } from '../constants';
import { CustomGridApi, FetchDataParams } from '../types';
import { prepareFilters, prepareSort } from '../utils';

interface UseTableFetchParams {
 fetchData?: (params: FetchDataParams) => void;
 fetchDataParams?: Record<string, unknown>;
 apiRef: MutableRefObject<CustomGridApi>;
 structure: StructureType[];
 currentColumnsPreset: ColPresetType | null;
 paginationModel: GridPaginationModel;
 isInit: boolean;
 loading: boolean;
 filter: GridFilterModel | undefined;
 sortModel: GridSortModel;
 loadAllClientSide?: boolean;
}

const useTableFetch = ({
 fetchData,
 fetchDataParams,
 apiRef,
 structure,
 currentColumnsPreset,
 paginationModel,
 isInit,
 loading,
 filter,
 sortModel,
 loadAllClientSide = false,
}: UseTableFetchParams) => {
 // Client-side grids fetch the whole dataset exactly once.
 const hasFetchedAllRef = useRef(false);

 const wrapFetchData = useCallback(() => {
  if (typeof fetchData !== 'function') return false;

  const presetFields =
   currentColumnsPreset?.columns
    ?.map((column) => column.field)
    .filter(Boolean) || [];

  const fields = Array.from(new Set(['Id', ...presetFields])).join(',');

  const filters = prepareFilters(
   apiRef.current.state.filter.filterModel.items,
   structure,
  );
  const sort = prepareSort(apiRef.current.getSortModel(), structure);

  fetchData({
   ...fetchDataParams,
   ...(presetFields.length > 0 ? { fields } : {}),
   sort,
   limit: loadAllClientSide ? CLIENT_LOAD_ALL_LIMIT : paginationModel.pageSize,
   skip: loadAllClientSide ? 0 : paginationModel.page * paginationModel.pageSize,
   ...filters,
  });

  return true;
 }, [
  fetchData,
  fetchDataParams,
  currentColumnsPreset,
  paginationModel,
  structure,
  apiRef,
  loadAllClientSide,
 ]);

 useEffect(() => {
  if (!(isInit && !loading && structure.length > 0)) return;
  // Client-side mode: load everything once, then let the grid paginate/sort/
  // filter locally without hitting the API again.
  if (loadAllClientSide && hasFetchedAllRef.current) return;
  if (loadAllClientSide) hasFetchedAllRef.current = true;
  wrapFetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [isInit, filter, sortModel, paginationModel, structure]);

 return { wrapFetchData };
};

export default useTableFetch;

